import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { FileUp, Send, X } from 'lucide-react';
import { useState, type DragEvent, type FormEvent } from 'react';
import { useNavigate } from 'react-router';
import { FloorplanPanel } from '../../components/floorplan-editor';
import { useToast } from '../../components/toast';
import { Alert, Button, Card, CardHeader, cn, ErrorState, Field, Input, PageHeader, PageLoader, Select, Textarea } from '../../components/ui';
import { api, ApiError } from '../../lib/api';
import { EVENT_TYPES, localInputToIso, MAX_ATTACHMENT_BYTES, uploadEventAttachment, type EventRequest, type EventRequestOptions } from '../../lib/events';
import { formatBytes } from '../../lib/facilities';
import type { Floorplan } from '../../lib/floorplan';

interface Needs {
  chosen: string[];
  notes: string;
  /** "Need setup by", as a datetime-local value. */
  neededBy: string;
}

const NO_NEEDS: Needs = { chosen: [], notes: '', neededBy: '' };

const EMPTY_FORM = { title: '', eventType: '', attendees: '', startsAt: '', setupAt: '', breakdownAt: '', buildingId: '', roomId: '', description: '' };

interface Problem {
  title: string;
  messages: string[];
}

/** What went wrong, with each field's message when the server named fields. */
function problemOf(err: unknown): Problem {
  if (err instanceof ApiError && err.issues?.length) {
    return { title: 'Check these and send the request again', messages: [...new Set(err.issues.map((issue) => issue.message))] };
  }
  return { title: err instanceof Error ? err.message : 'Something went wrong', messages: [] };
}

/** The event setup request form: the event, what each team needs ready by when, and an attachment. */
export function EventRequestFormPage() {
  const navigate = useNavigate();
  const toast = useToast();
  const queryClient = useQueryClient();
  const options = useQuery({ queryKey: ['event-request-options'], queryFn: () => api.get<EventRequestOptions>('/event-requests/options') });
  const [form, setForm] = useState(EMPTY_FORM);
  const [tech, setTech] = useState<Needs>(NO_NEEDS);
  const [maintenance, setMaintenance] = useState<Needs>(NO_NEEDS);
  const [file, setFile] = useState<File | null>(null);
  const [floorplan, setFloorplan] = useState<Floorplan | null>(null);
  const [dragging, setDragging] = useState(false);
  const [problem, setProblem] = useState<Problem | null>(null);
  const update = (patch: Partial<typeof EMPTY_FORM>) => setForm((current) => ({ ...current, ...patch }));

  const showProblem = (next: Problem) => {
    setProblem(next);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const send = useMutation({
    mutationFn: async () => {
      const attachment = file ? await uploadEventAttachment(file) : null;
      return api.post<EventRequest>('/event-requests', {
        ...form,
        roomId: form.roomId || null,
        startsAt: localInputToIso(form.startsAt),
        setupAt: localInputToIso(form.setupAt),
        breakdownAt: localInputToIso(form.breakdownAt),
        techNeeds: tech.chosen,
        techNotes: tech.notes,
        techNeededBy: tech.chosen.length > 0 ? localInputToIso(tech.neededBy) : null,
        facilitiesNeeds: maintenance.chosen,
        facilitiesNotes: maintenance.notes,
        facilitiesNeededBy: maintenance.chosen.length > 0 ? localInputToIso(maintenance.neededBy) : null,
        attachmentId: attachment?.id ?? null,
        floorplan,
      });
    },
    onSuccess: (event) => {
      void queryClient.invalidateQueries({ queryKey: ['event-requests'] });
      void queryClient.invalidateQueries({ queryKey: ['work-orders'] });
      const numbers = event.workOrders.map((ticket) => `#${ticket.number}`).join(' and ');
      toast.success(numbers ? `Request sent. Opened work order ${numbers}.` : 'Request sent');
      navigate(`/events/${event.id}`);
    },
    onError: (err) => showProblem(problemOf(err)),
  });

  const chooseFile = (next: File | null | undefined) => {
    if (!next) return;
    if (next.size > MAX_ATTACHMENT_BYTES) return showProblem({ title: `${next.name} is ${formatBytes(next.size)}. Attachments can be up to 25 MB.`, messages: [] });
    if (next.size === 0) return showProblem({ title: `${next.name} is empty. Choose another file.`, messages: [] });
    setProblem(null);
    setFile(next);
  };

  const onDrop = (event: DragEvent<HTMLLabelElement>) => {
    event.preventDefault();
    setDragging(false);
    chooseFile(event.dataTransfer.files?.[0]);
  };

  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (tech.chosen.length === 0 && maintenance.chosen.length === 0) {
      showProblem({ title: 'Choose at least one tech or maintenance need, so a team gets a work order.', messages: [] });
      return;
    }
    setProblem(null);
    send.mutate();
  };

  if (options.isLoading) return <PageLoader />;
  if (options.error || !options.data) return <ErrorState error={options.error} onRetry={() => options.refetch()} />;
  const { places, techNeeds, facilitiesNeeds } = options.data;
  const rooms = places.find((building) => building.id === form.buildingId)?.rooms ?? [];

  return (
    <>
      <PageHeader
        back={{ to: '/events', label: 'Event requests' }}
        title="Request an event setup"
        description="Tell the technology team and facilities what your event needs. Each team you ask for something gets a work order with these details."
      />

      <form onSubmit={submit} className="max-w-4xl space-y-6">
        {problem && (
          <Alert tone="red" title={problem.title}>
            {problem.messages.length > 0 && (
              <ul className="list-disc space-y-0.5 pl-5">
                {problem.messages.map((message) => (
                  <li key={message}>{message}</li>
                ))}
              </ul>
            )}
          </Alert>
        )}

        <Card>
          <CardHeader title="Event" />
          <div className="space-y-4 p-5">
            <Field label="Event title" required>
              <Input value={form.title} onChange={(e) => update({ title: e.target.value })} maxLength={150} placeholder="e.g. Spring concert" required />
            </Field>
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Event type" required hint="Choose one or type your own">
                <Input list="event-types" value={form.eventType} onChange={(e) => update({ eventType: e.target.value })} maxLength={80} required />
                <datalist id="event-types">
                  {EVENT_TYPES.map((type) => (
                    <option key={type} value={type} />
                  ))}
                </datalist>
              </Field>
              <Field label="Number of attendees" required>
                <Input type="number" inputMode="numeric" min={1} max={100000} value={form.attendees} onChange={(e) => update({ attendees: e.target.value })} required />
              </Field>
            </div>
            <div className="grid gap-4 sm:grid-cols-3">
              <Field label="Date & time" required>
                <Input type="datetime-local" value={form.startsAt} onChange={(e) => update({ startsAt: e.target.value })} required />
              </Field>
              <Field label="Setup time" required hint="When setup starts">
                <Input type="datetime-local" value={form.setupAt} max={form.startsAt || undefined} onChange={(e) => update({ setupAt: e.target.value })} required />
              </Field>
              <Field label="Breakdown time" required hint="When breakdown starts">
                <Input type="datetime-local" value={form.breakdownAt} min={form.startsAt || undefined} onChange={(e) => update({ breakdownAt: e.target.value })} required />
              </Field>
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Building" required>
                <Select value={form.buildingId} onChange={(e) => update({ buildingId: e.target.value, roomId: '' })} required>
                  <option value="">Choose a building</option>
                  {places.map((building) => (
                    <option key={building.id} value={building.id}>
                      {building.name}
                    </option>
                  ))}
                </Select>
              </Field>
              <Field label="Room">
                <Select value={form.roomId} disabled={!form.buildingId} onChange={(e) => update({ roomId: e.target.value })}>
                  <option value="">{form.buildingId ? 'No particular room' : 'Choose a building first'}</option>
                  {rooms.map((room) => (
                    <option key={room.id} value={room.id}>
                      {room.name}
                    </option>
                  ))}
                </Select>
              </Field>
            </div>
            <Field label="Event description">
              <Textarea
                rows={4}
                maxLength={5000}
                value={form.description}
                onChange={(e) => update({ description: e.target.value })}
                placeholder="What’s happening, and anything the teams should know"
              />
            </Field>
          </div>
        </Card>

        <NeedsCard
          title="Tech needs"
          description="Choosing any of these opens a work order for the technology team."
          options={techNeeds}
          value={tech}
          onChange={setTech}
          setupAt={form.setupAt}
        />
        {facilitiesNeeds && (
          <NeedsCard
            title="Maintenance needs"
            description="Choosing any of these opens a work order for facilities."
            options={facilitiesNeeds}
            value={maintenance}
            onChange={setMaintenance}
            setupAt={form.setupAt}
          />
        )}

        <Card>
          <CardHeader title="Floorplan" description="Sketch how the space should be set up: the stage, tables, chairs, and where things go." />
          <div className="p-5">
            <FloorplanPanel
              plan={floorplan}
              canEdit
              onChange={setFloorplan}
              name={form.title || 'Event'}
              emptyText="Optional. A quick sketch helps each team set up the space the same way."
            />
          </div>
        </Card>

        <Card>
          <CardHeader title="Attachment" description="A run of show, a detailed plan, or anything else that helps, up to 25 MB." />
          <div className="p-5">
            {file ? (
              <div className="flex items-center justify-between gap-3 rounded-lg border border-slate-200 px-4 py-3 text-sm">
                <div className="min-w-0">
                  <p className="truncate font-medium text-slate-900">{file.name}</p>
                  <p className="text-xs text-slate-500">{formatBytes(file.size)}</p>
                </div>
                <Button type="button" size="sm" variant="ghost" icon={<X />} onClick={() => setFile(null)}>
                  Remove
                </Button>
              </div>
            ) : (
              <label
                onDragOver={(event) => {
                  event.preventDefault();
                  setDragging(true);
                }}
                onDragLeave={(event) => {
                  if (!event.currentTarget.contains(event.relatedTarget as Node | null)) setDragging(false);
                }}
                onDrop={onDrop}
                className={cn(
                  'flex cursor-pointer flex-col items-center justify-center rounded-xl border-2 border-dashed px-6 py-8 text-center transition-colors',
                  dragging ? 'border-brand-500 bg-brand-50/60' : 'border-slate-300 hover:border-brand-400 hover:bg-brand-50/30',
                )}
              >
                <FileUp className="size-8 text-slate-400" />
                <span className="mt-2 text-sm font-medium text-slate-800">Choose a file or drop it here</span>
                <span className="mt-1 text-xs text-slate-500">Optional</span>
                <input
                  type="file"
                  className="sr-only"
                  onChange={(event) => {
                    chooseFile(event.target.files?.[0]);
                    event.target.value = '';
                  }}
                />
              </label>
            )}
          </div>
        </Card>

        <div className="flex flex-wrap justify-end gap-2">
          <Button type="button" onClick={() => navigate('/events')}>
            Cancel
          </Button>
          <Button type="submit" variant="primary" icon={<Send />} loading={send.isPending}>
            Send request
          </Button>
        </div>
      </form>
    </>
  );
}

/** One team's needs: checkboxes from its list, then notes and when it needs to be ready once something is chosen. */
function NeedsCard({
  title,
  description,
  options,
  value,
  onChange,
  setupAt,
}: {
  title: string;
  description: string;
  options: string[];
  value: Needs;
  onChange(next: Needs): void;
  /** The event's setup time, which "Need setup by" starts at. */
  setupAt: string;
}) {
  const toggle = (need: string, checked: boolean) => {
    const chosen = checked ? [...value.chosen, need] : value.chosen.filter((item) => item !== need);
    onChange({ ...value, chosen, neededBy: value.neededBy || (checked ? setupAt : '') });
  };

  return (
    <Card>
      <CardHeader title={title} description={description} />
      <div className="space-y-4 p-5">
        {options.length === 0 ? (
          <p className="text-sm text-slate-500">There’s nothing to choose from yet.</p>
        ) : (
          <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
            {options.map((need) => (
              <label
                key={need}
                className="flex cursor-pointer items-center gap-2.5 rounded-lg border border-slate-200 px-3 py-2.5 text-sm text-slate-800 transition-colors hover:bg-slate-50 has-[:checked]:border-brand-300 has-[:checked]:bg-brand-50/60"
              >
                <input type="checkbox" className="size-4 rounded border-slate-300" checked={value.chosen.includes(need)} onChange={(e) => toggle(need, e.target.checked)} />
                {need}
              </label>
            ))}
          </div>
        )}
        {value.chosen.length > 0 && (
          <div className="grid gap-4 sm:grid-cols-3">
            <div className="sm:col-span-2">
              <Field label="Additional notes">
                <Textarea
                  rows={3}
                  maxLength={2000}
                  value={value.notes}
                  onChange={(e) => onChange({ ...value, notes: e.target.value })}
                  placeholder="Quantities, placement, or anything else they should know"
                />
              </Field>
            </div>
            <Field label="Need setup by" required>
              <Input type="datetime-local" value={value.neededBy} onChange={(e) => onChange({ ...value, neededBy: e.target.value })} required />
            </Field>
          </div>
        )}
      </div>
    </Card>
  );
}
